// frontend/src/components/ChatInput.tsx
import React, { useState, FormEvent, useEffect } from 'react';
import Form from 'react-bootstrap/Form';
import InputGroup from 'react-bootstrap/InputGroup';
import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';
import { Send, MicFill } from 'react-bootstrap-icons';
import TextareaAutosize from 'react-textarea-autosize';
import { useAudioRecorder } from '../hooks/useAudioRecorder';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

interface ChatInputProps {
    onSendMessage: (message: string) => void;
    isLoading: boolean;
}

export function ChatInput({ onSendMessage, isLoading }: ChatInputProps) {
    const [input, setInput] = useState('');
    const [isTranscribing, setIsTranscribing] = useState(false);

    const {
        isRecording,
        audioBlob,
        recordingError,
        startRecording,
        stopRecording,
        resetAudio
    } = useAudioRecorder();

    useEffect(() => {
        if (audioBlob) {
            sendAudioToBackend(audioBlob);
            resetAudio();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [audioBlob, resetAudio]);

    const sendAudioToBackend = async (blob: Blob) => {
        setIsTranscribing(true);
        const formData = new FormData();
        formData.append('audioFile', blob, `recording-${Date.now()}.webm`);

        try {
            const response = await fetch(`${API_BASE_URL}/api/audio/transcribe`, {
                method: 'POST',
                body: formData,
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(`Error del servidor: ${response.statusText} - ${errorData.error || 'Sin detalles'}`);
            }

            const result = await response.json();
            if (result.transcription) {
                setInput(prevInput => prevInput + (prevInput ? ' ' : '') + result.transcription.trim());
            } else {
                // Puedes mostrar un mensaje al usuario si lo deseas
            }
        } catch (error) {
            alert(`Error al procesar audio: ${error instanceof Error ? error.message : String(error)}`);
        } finally {
            setIsTranscribing(false);
        }
    };

    const handleMicClick = () => {
        if (isRecording) {
            stopRecording();
        } else {
            startRecording();
        }
    };

    const handleSubmit = (e: FormEvent) => {
        e.preventDefault();
        if (!input.trim() || isLoading || isRecording || isTranscribing) return;
        onSendMessage(input);
        setInput('');
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey && !isRecording && !isTranscribing) {
            e.preventDefault();
            handleSubmit(e as any);
        }
    };

    const sendDisabled = isLoading || !input.trim() || isRecording || isTranscribing;
    const micDisabled = isTranscribing;
    const inputDisabled = isLoading || isRecording || isTranscribing;

    return (
        <Form onSubmit={handleSubmit} className="mt-2 chat-input-container">
            {recordingError && <div className="alert alert-warning p-1 small mb-1">{recordingError}</div>}
            <InputGroup>
                <TextareaAutosize
                    className="form-control"
                    minRows={1}
                    maxRows={5}
                    style={{ resize: 'none' }}
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Escribe o presiona el micrófono..."
                    disabled={inputDisabled}
                    aria-label="Mensaje a enviar"
                />
                <Button
                    variant={isRecording ? "danger" : "secondary"}
                    onClick={handleMicClick}
                    disabled={micDisabled}
                    className="ms-1"
                    aria-label={isRecording ? "Detener grabación" : "Iniciar grabación"}
                >
                    {isTranscribing ? <Spinner animation="border" size="sm"/> : <MicFill size={20}/>}
                </Button>
                <Button
                    variant="primary"
                    type="submit"
                    disabled={sendDisabled}
                    style={{ alignSelf: 'flex-end' }}
                    className="ms-1"
                    aria-label="Enviar mensaje"
                >
                    {isLoading ? <Spinner animation="border" size="sm"/> : <Send size={20}/>}
                </Button>
            </InputGroup>
        </Form>
    );
}